import { format } from './format';
import { catalogs, DEFAULT_LOCALE, type LocaleCode } from './locales';
import type { MessageKey, MessageVars, Translate } from './types';

const VAR_PATTERN = /\{\{(\w+)\}\}/g;

export const interpolate = (template: string, vars?: MessageVars): string => {
  if (!vars) {
    return template;
  }

  return template.replace(VAR_PATTERN, (match, key: string) => {
    const value = vars[key];
    return value == null ? match : String(value);
  });
};

const getPath = (source: unknown, key: string): unknown =>
  key.split('.').reduce<unknown>((current, segment) => {
    if (current == null || typeof current !== 'object') {
      return undefined;
    }
    return (current as Record<string, unknown>)[segment];
  }, source);

export const lookupMessage = (key: string, locale: LocaleCode = DEFAULT_LOCALE): unknown => {
  const fromLocale = getPath(catalogs[locale], key);
  if (fromLocale !== undefined) {
    return fromLocale;
  }
  return getPath(catalogs[DEFAULT_LOCALE], key);
};

/**
 * Translate a catalog string. Keys are dotted paths into `language-REGION.json`
 * (`t('confirm.leavePage.title')`). Unknown keys return the key.
 */
export const t = (key: MessageKey | string, vars?: MessageVars, locale: LocaleCode = DEFAULT_LOCALE): string => {
  const value = lookupMessage(key, locale);
  if (typeof value !== 'string') {
    return key;
  }
  return interpolate(value, vars);
};

/** Translator bound to one locale. Pass this into helpers instead of reading a shared locale. */
export const createTranslator = (locale: LocaleCode): Translate => {
  const translate = ((key: MessageKey | string, vars?: MessageVars) => t(key, vars, locale)) as Translate;
  translate.format = (key, vars) => format(t(key, vars, locale));
  return translate;
};
